import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, Users, Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../App';

const AdminA3ResultsPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(null);

  useEffect(() => {
    API.get('/admin/assessment/a3/results')
      .then(({ data }) => setRows(data))
      .catch((err) => toast.error(err?.response?.data?.detail || 'Failed to load.'))
      .finally(() => setLoading(false));
  }, []);

  const resend = async (id) => {
    setResending(id);
    try {
      const { data } = await API.post(`/admin/assessment/a3/results/${id}/resend`);
      toast.success(data.message || 'Emails re-queued.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to resend.');
    } finally {
      setResending(null);
    }
  };

  const exportCsv = () => {
    const header = ['created_at', 'name', 'email', 'handicap', 'age_range', 'playing_level', 'competitive', 'awareness', 'acceptance', 'action', 'overall', 'profile_type', 'focus'];
    const lines = [header.join(',')];
    for (const r of rows) {
      const d = r.demographics || {};
      const s = r.result?.sections || [];
      const find = (k) => (s.find((x) => x.key === k) || {}).score;
      lines.push([
        r.created_at,
        JSON.stringify(d.name || ''),
        JSON.stringify(d.email || ''),
        JSON.stringify(d.handicap || ''),
        JSON.stringify(d.age_range || ''),
        JSON.stringify(d.playing_level || ''),
        JSON.stringify(d.competitive || ''),
        find('awareness') ?? '',
        find('acceptance') ?? '',
        find('action') ?? '',
        r.result?.overall?.score ?? '',
        r.result?.profile_type ?? '',
        r.result?.focus?.section ?? '',
      ].join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `a3-results-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] py-10">
      <div className="container mx-auto px-6 md:px-12 max-w-6xl">
        <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-[#57534E] hover:text-[#1A1A1A] mb-6"><ArrowLeft className="w-4 h-4" /> Admin</Link>
        <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
          <div>
            <p className="text-[#BC562B] text-xs uppercase tracking-[0.35em] mb-2 flex items-center gap-2"><Users className="w-4 h-4" /> A³ Assessment</p>
            <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1A1A1A]">Submissions</h1>
          </div>
          <button onClick={exportCsv} disabled={!rows.length} className="inline-flex items-center gap-2 bg-[#1A2E1D] text-white px-4 py-2 text-xs uppercase tracking-wider font-semibold hover:bg-[#0F1F12] disabled:opacity-50" data-testid="a3-export-csv">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>

        {loading ? (
          <div className="text-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#BC562B] mx-auto" /></div>
        ) : rows.length === 0 ? (
          <p className="text-[#A8A29E]">No submissions yet.</p>
        ) : (
          <div className="bg-white border border-[#E7E5E4] overflow-x-auto" data-testid="a3-admin-table">
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAF9] text-[#57534E]">
                <tr className="text-xs uppercase tracking-wider">
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">HCP</th>
                  <th className="text-right px-3 py-3">Aw.</th>
                  <th className="text-right px-3 py-3">Ac.</th>
                  <th className="text-right px-3 py-3">Act.</th>
                  <th className="text-right px-3 py-3">A³</th>
                  <th className="text-left px-4 py-3">Profile</th>
                  <th className="text-left px-4 py-3">Focus</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const s = r.result?.sections || [];
                  const find = (k) => (s.find((x) => x.key === k) || {}).score;
                  return (
                    <tr key={r.id} className="border-t border-[#F2F0E9]" data-testid={`a3-row-${r.id}`}>
                      <td className="px-4 py-3 whitespace-nowrap text-[#57534E]">{new Date(r.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })}</td>
                      <td className="px-4 py-3">
                        <div className="text-[#1A1A1A] font-semibold">{r.demographics?.name}</div>
                        <div className="text-xs text-[#A8A29E]">{r.demographics?.email}</div>
                      </td>
                      <td className="px-4 py-3 text-[#57534E]">{r.demographics?.handicap || '—'}</td>
                      <td className="px-3 py-3 text-right font-semibold text-[#2C5530]">{find('awareness')}</td>
                      <td className="px-3 py-3 text-right font-semibold text-[#BC562B]">{find('acceptance')}</td>
                      <td className="px-3 py-3 text-right font-semibold text-[#1A2E1D]">{find('action')}</td>
                      <td className="px-3 py-3 text-right font-['Playfair_Display'] text-xl text-[#1A1A1A]">{r.result?.overall?.score}</td>
                      <td className="px-4 py-3 text-xs uppercase tracking-wider text-[#57534E]">{r.result?.profile_type}</td>
                      <td className="px-4 py-3 text-xs uppercase tracking-wider text-[#BC562B] font-semibold">{r.result?.focus?.section}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button
                          onClick={() => resend(r.id)}
                          disabled={resending === r.id}
                          className="text-[#57534E] hover:text-[#1A1A1A] text-xs uppercase tracking-wider inline-flex items-center gap-1 mr-3 disabled:opacity-50"
                          title="Resend welcome + results emails"
                          data-testid={`a3-resend-${r.id}`}
                        >
                          {resending === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />} Resend
                        </button>
                        <Link to={`/a3-assessment/result/${r.id}`} className="text-[#BC562B] text-xs uppercase tracking-wider hover:underline">View →</Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminA3ResultsPage;
